<script setup>
/**
 * "SUGGESTED BY YOUR TEAM" (spec §1 P-1.7; §9.1 I-3's one review list).
 *
 * What the team suggested from the chat pane, waiting on the owner. Accept teaches it, which is
 * an AI step (one embedding), so the button wears the price and asks first, exactly like
 * "Teach the right answer" (P-5.12); Cancel in that dialog leaves everything as it was. Edit
 * lets her change the words first. Dismiss changes nothing in what the assistant knows.
 *
 * Hidden when nothing is waiting and the scan state cannot be read: an empty review list is
 * not something to show on Answers. C3 adds pairs mined from the team's replies to the same
 * list (`origin: 'staff_reply'`), and the "Look through my team's replies" control (P-6.5),
 * which keeps the section on screen whenever the day's scan state has been read.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import AiConfirmDialog from './AiConfirmDialog.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { WASLO_ERROR_CODES, wasloApi, withConfirm } from '../api/client.js';
import { receiptPhrase } from '../helpers/ai-price.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  accountId: { type: Number, default: null },
});

const emit = defineEmits(['changed']);

const { t, locale } = useWasloI18n();

const list = useWasloApi(
  signal => wasloApi.getTeamSuggestions(props.accountId, signal),
  { isEmpty: () => false }
);

/** Rows decided here, gone from the list before the next read says so. */
const decided = ref(new Set());
const rows = computed(() =>
  (list.data.value?.items ?? []).filter(row => !decided.value.has(row.id))
);

// ── P-6.5: "Look through my team's replies", once a day, priced, and its own rows ────────
const scan = useWasloApi(
  signal => wasloApi.getScanState(props.accountId, signal),
  { isEmpty: () => false }
);
const scanCharge = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'scan_team_replies',
});
const isStarting = ref(false);
const scanFailure = ref('');
const formatCount = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

/** "From your team's replies · Came up in 3 chats": where a scanned row came from. */
const staffLine = row =>
  [
    t('WASLO.SCAN.FROM_REPLIES'),
    t(
      'WASLO.SCAN.CAME_UP',
      { count: formatCount(row.chatCount) },
      row.chatCount
    ),
  ].join(' · ');

/** One sentence for the last scan of today, or none before the first one. */
const scanLine = computed(() => {
  const state = scan.data.value;
  if (scanFailure.value) return scanFailure.value;
  if (!state?.last || state.last.day !== state.today) return '';
  if (state.last.state === 'running') return t('WASLO.SCAN.RUNNING');
  if (state.last.state === 'failed') return t('WASLO.SCAN.FAILED');
  if (state.last.suggested === 0) return t('WASLO.SCAN.NOTHING');
  return t(
    'WASLO.SCAN.DONE',
    { count: formatCount(state.last.suggested) },
    state.last.suggested
  );
});
const canScan = computed(() => scan.data.value?.canScan === true);
const scanRunning = computed(
  () =>
    scan.data.value?.last?.state === 'running' &&
    scan.data.value?.last?.day === scan.data.value?.today
);

/**
 * While today's scan runs, read its state again every few seconds; when it lands, read the
 * list once, so the answers it found appear without a reload.
 */
const SCAN_POLL_MS = 5000;
let scanTimer = null;
let wasRunning = false;
watch(
  () => scan.data.value,
  () => {
    clearTimeout(scanTimer);
    if (scanRunning.value) {
      wasRunning = true;
      scanTimer = setTimeout(() => scan.reload(), SCAN_POLL_MS);
    } else if (wasRunning) {
      wasRunning = false;
      list.reload();
    }
  }
);
onBeforeUnmount(() => clearTimeout(scanTimer));

const startScan = async () => {
  if (isStarting.value || props.accountId === null || !canScan.value) return;
  isStarting.value = true;
  scanFailure.value = '';
  try {
    const outcome = await scanCharge.confirmAndRun({
      run: credits =>
        wasloApi.startScan(props.accountId, undefined, withConfirm(credits)),
    });
    if (outcome.cancelled) return;
    scan.reload();
  } catch (error) {
    scanFailure.value =
      error?.status === 409 &&
      error?.code !== WASLO_ERROR_CODES.CONFIRM_REQUIRED
        ? t('WASLO.SCAN.TOMORROW')
        : t('WASLO.SCAN.FAILED');
  } finally {
    isStarting.value = false;
  }
};

const acceptCharge = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'teach_answer',
});
const acceptLabel = computed(() =>
  acceptCharge.label(t('WASLO.ANSWERS.TEAM.ACCEPT'))
);

/** The row being edited, and its two boxes. */
const editing = ref(null);
const draft = ref({ question: '', answer: '' });
const busy = ref(null);
/** One line under the list: what the last action did. */
const done = ref('');

const drop = id => {
  decided.value = new Set([...decided.value, id]);
  emit('changed');
};

const startEdit = row => {
  editing.value = row.id;
  draft.value = { question: row.question, answer: row.answer };
};

const stopEdit = () => {
  editing.value = null;
};

const failed = error => {
  if (
    error?.status === 409 &&
    error?.code !== WASLO_ERROR_CODES.CONFIRM_REQUIRED
  ) {
    return t('WASLO.ANSWERS.TEAM.GONE');
  }
  return t('WASLO.ANSWERS.TEAM.FAILED');
};

const accept = async row => {
  if (busy.value !== null || props.accountId === null) return;
  busy.value = row.id;
  done.value = '';
  const edits =
    editing.value === row.id
      ? { question: draft.value.question, answer: draft.value.answer }
      : {};
  try {
    const outcome = await acceptCharge.confirmAndRun({
      run: credits =>
        wasloApi.acceptTeamSuggestion(
          props.accountId,
          row.id,
          edits,
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    drop(row.id);
    stopEdit();
    const receipt = receiptPhrase(t, locale.value, outcome.result.charged);
    done.value = [t('WASLO.ANSWERS.TEAM.ACCEPTED'), receipt]
      .filter(Boolean)
      .join(' ');
  } catch (error) {
    done.value = failed(error);
    if (error?.status === 409) drop(row.id);
  } finally {
    busy.value = null;
  }
};

const dismiss = async row => {
  if (busy.value !== null || props.accountId === null) return;
  busy.value = row.id;
  done.value = '';
  try {
    await wasloApi.dismissTeamSuggestion(props.accountId, row.id);
    drop(row.id);
    done.value = t('WASLO.ANSWERS.TEAM.DISMISSED');
  } catch (error) {
    done.value = failed(error);
    if (error?.status === 409) drop(row.id);
  } finally {
    busy.value = null;
  }
};
</script>

<template>
  <section
    v-if="rows.length || done || scan.data.value"
    class="flex flex-col gap-4"
    data-waslo-team-suggestions
  >
    <SettingsHeader
      :heading="t('WASLO.ANSWERS.TEAM.HEADING')"
      :description="t('WASLO.ANSWERS.TEAM.DESCRIPTION')"
    />
    <ul
      v-if="rows.length"
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
    >
      <li
        v-for="row in rows"
        :key="row.id"
        class="flex flex-col gap-3 px-5 py-4"
        :data-waslo-team-suggestion="row.id"
      >
        <p
          v-if="row.origin === 'staff_reply'"
          class="mb-0 text-xs text-n-slate-10"
          data-waslo-team-from-replies
        >
          {{ staffLine(row) }}
        </p>
        <p
          v-else-if="row.suggestedBy"
          class="mb-0 text-xs text-n-slate-10"
          dir="auto"
        >
          {{ t('WASLO.ANSWERS.TEAM.BY', { name: row.suggestedBy.name }) }}
        </p>
        <template v-if="editing === row.id">
          <WasloTextArea
            v-model="draft.question"
            :label="t('WASLO.ANSWERS.TEAM.QUESTION_LABEL')"
            :max-length="500"
            min-height="3rem"
          />
          <WasloTextArea
            v-model="draft.answer"
            :label="t('WASLO.ANSWERS.TEAM.ANSWER_LABEL')"
            :max-length="4000"
            min-height="5rem"
          />
        </template>
        <template v-else>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-10">
              {{ t('WASLO.ANSWERS.TEAM.QUESTION_LABEL') }}
            </span>
            <bdi class="text-sm break-words text-n-slate-12">
              {{ row.question }}
            </bdi>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-10">
              {{ t('WASLO.ANSWERS.TEAM.ANSWER_LABEL') }}
            </span>
            <bdi
              class="text-sm break-words whitespace-pre-line text-n-slate-12"
            >
              {{ row.answer }}
            </bdi>
          </div>
        </template>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            :label="
              editing === row.id
                ? acceptCharge.label(t('WASLO.ANSWERS.TEAM.ACCEPT_EDITED'))
                : acceptLabel
            "
            size="sm"
            :is-loading="busy === row.id"
            data-waslo-team-accept
            @click="accept(row)"
          />
          <Button
            v-if="editing !== row.id"
            :label="t('WASLO.ANSWERS.TEAM.EDIT')"
            size="sm"
            variant="faded"
            color="slate"
            data-waslo-team-edit
            @click="startEdit(row)"
          />
          <Button
            v-else
            :label="t('WASLO.ANSWERS.TEAM.CANCEL')"
            size="sm"
            variant="faded"
            color="slate"
            @click="stopEdit"
          />
          <Button
            :label="t('WASLO.ANSWERS.TEAM.DISMISS')"
            size="sm"
            variant="ghost"
            color="slate"
            data-waslo-team-dismiss
            @click="dismiss(row)"
          />
        </div>
      </li>
    </ul>
    <p
      v-if="done"
      class="mb-0 text-sm text-n-slate-11"
      aria-live="polite"
      data-waslo-team-done
    >
      {{ done }}
    </p>
    <div
      v-if="scan.data.value"
      class="flex flex-wrap items-center gap-3"
      data-waslo-team-scan
    >
      <Button
        :label="scanCharge.label(t('WASLO.SCAN.START'))"
        size="sm"
        variant="faded"
        color="slate"
        :disabled="!canScan || scanRunning"
        :is-loading="isStarting"
        data-waslo-team-scan-start
        @click="startScan"
      />
      <p
        v-if="!canScan && !scanRunning && !scanLine"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('WASLO.SCAN.TOMORROW') }}
      </p>
      <p
        v-if="scanLine"
        class="mb-0 text-sm text-n-slate-11"
        aria-live="polite"
        data-waslo-team-scan-line
      >
        {{ scanLine }}
      </p>
    </div>
    <AiConfirmDialog :controller="acceptCharge" />
    <AiConfirmDialog :controller="scanCharge" />
  </section>
</template>
